// src/components/ActivatedOffer.tsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Delete, ExpandMore } from "@mui/icons-material";
import type { Offer } from "../../lib/types/detailsTypes";
import { expireOffer } from "./offersFirestore";
import NearbyOfferLocationsButton from "../../reusables/nearbyLocationsButton";

interface ActivatedOfferProps {
  open: boolean;
  offer: Offer;
  onClose: () => void;
  onOfferDeleted?: (offerId: string) => void;
}

const ActivatedOffer: React.FC<ActivatedOfferProps> = ({
  open,
  offer,
  onClose,
  onOfferDeleted,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  // Tracks the formatted time (e.g., "00:29:53")
  const [timeLeft, setTimeLeft] = useState("00:30:00");
  // Tracks whether the timer has fully expired
  const [isTimeUp, setIsTimeUp] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  /** Handles closing the main offer dialog's snackbar. */
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    // If no activatedTime or dialog isn't open, don't start the countdown
    if (!open || !offer.activatedTime) return;

    const activatedDate = offer.activatedTime.toDate();
    // 30 minutes after activation
    const expirationDate = new Date(activatedDate.getTime() + 30 * 60_000);

    const updateCountdown = () => {
      const now = new Date();
      const diff = expirationDate.getTime() - now.getTime();

      if (diff <= 0) {
        // Time is up
        setTimeLeft("00:00:00");
        setIsTimeUp(true);
        return;
      }

      const hours = Math.floor(diff / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      const seconds = Math.floor((diff % 60_000) / 1_000);

      // Pad with zeros for hh:mm:ss
      const hh = String(hours).padStart(2, "0");
      const mm = String(minutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");

      setTimeLeft(`${hh}:${mm}:${ss}`);
      setIsTimeUp(false);
    };

    // Update immediately on mount
    updateCountdown();

    // Update every second
    const timerId = setInterval(updateCountdown, 1000);
    return () => clearInterval(timerId);
  }, [open, offer.activatedTime]);

  /** Opens the "Confirm Delete" dialog. */
  const openDeleteDialog = () => {
    if (isTimeUp) {
      handleDeleteConfirm();
      // Close the main dialog
      onClose();
    } else {
      setDeleteDialogOpen(true);
    }
  };

  /** Closes the "Confirm Delete" dialog. */
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  /** Final step: user confirmed deletion. */
  const handleDeleteConfirm = async () => {
    setIsProcessing(true);
    setDeleteDialogOpen(false);

    const { success, message } = await expireOffer(offer.id);
    setSnackbarMessage(message);
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);

    if (success) {
      // Notify parent component about deletion
      onOfferDeleted?.(offer.id);
      // Close the offer dialog after successful deletion
      onClose();
    }

    setIsProcessing(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 400,
            maxHeight: "80vh",
            borderRadius: 4,
            position: "relative",
            m: "auto",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{ position: "relative" }}>
          {offer.storeName}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              backgroundColor: "white",
              color: "black",
              borderRadius: "50%",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent
          sx={{
            overflowY: "auto",
            flex: 1,
            px: 2,
            pt: 0,
            pb: 2,
          }}
        >
          {/* Container for spinning glow + static countdown */}
          <Box
            sx={{
              position: "relative",
              width: 140,
              height: 140,
              margin: "20px auto",
            }}
          >
            {/* This Box is the rotating glow ring */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                // If time is up, turn the ring red; otherwise lime green
                border: `4px solid ${isTimeUp ? "red" : "limegreen"}`,
                boxShadow: `0 0 15px ${isTimeUp ? "red" : "limegreen"}`,
                animation: "spinGlow 2s linear infinite",
                "@keyframes spinGlow": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />

            {/* Inner circle that does NOT rotate (just for background) */}
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                right: 8,
                bottom: 8,
                borderRadius: "50%",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.4rem",
                  userSelect: "none",
                  color: isTimeUp ? "red" : "inherit",
                }}
              >
                {timeLeft}
              </Typography>
            </Box>
          </Box>
          {/* Offer Code */}
          <Typography variant="body1" align="center" gutterBottom>
            <strong>Offer Code:</strong> {offer.offerCode}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Title, bigger & centered */}
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{ fontWeight: "bold", my: 2 }}
          >
            {offer.title}
          </Typography>

          {/* Description Accordion */}
          {offer.description && (
            <Accordion disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="description-content"
                id="description-header"
              >
                <Typography variant="subtitle2">Description</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{offer.description}</Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Terms Accordion */}
          {offer.termsAndConditions && (
            <Accordion disableGutters sx={{ mt: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="terms-content"
                id="terms-header"
              >
                <Typography variant="subtitle2">Terms</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {offer.termsAndConditions}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
          sx={{
            flexDirection: "column",
            alignItems: "stretch",
            px: 2,
            position: "relative",
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              p: 1,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <NearbyOfferLocationsButton
              storeId={offer.storeId}
              acceptedLocationIds={offer.acceptedLocations}
            />
            <Button
              variant="contained"
              // If time is up, show a red "Expired" button
              color={isTimeUp ? "error" : "success"}
              disabled
              sx={{
                fontWeight: "bold",
                width: "100%",
                borderRadius: "999px",
                boxShadow: 3,
                py: 1.5,
              }}
            >
              {isTimeUp ? "Expired" : "Activated"}
            </Button>
            <IconButton
              onClick={openDeleteDialog}
              disabled={isProcessing}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: 3,
                p: 1,
              }}
            >
              <Delete color="error" />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirm Deletion Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this offer? This action is
            irreversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isProcessing}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ActivatedOffer;
